import { useEffect, useRef } from 'react';
import { useTriggerInsightsGenerationV1InsightsGeneratePost } from '@/api/generated-catch-backend/insights/insights';
import sentryService from '@/services/sentry.service';

const SESSION_FIRED_KEY = 'insights-bootstrap-fired';

/**
 * Fires `POST /v1/insights/generate` once per browser session, shortly after
 * the user lands in the authenticated app. The backend short-circuits on an
 * existing narrative row, so this is cheap when there's nothing to do —
 * meaningful only for members who completed RA before the narrative feature
 * shipped (no row on file) and need generation kicked off here.
 *
 * Goal: maximize the chance the narrative is ready by the time the user
 * navigates to Risk Analysis. We can't await it (the workflow takes 1-3 min
 * typically), so we fire-and-forget and let the Risk Analysis card's polling
 * loop catch the result whenever it lands.
 *
 * ⚠️ MUST be called from a post-auth context — i.e., a component that only
 * mounts after `<AuthGuard>` has confirmed `me` is loaded. Putting it on a
 * pre-auth layout (e.g., the sign-in / sign-up `<Layout>`) makes it fire for
 * anonymous visitors, who 401, hit the customFetcher's hard-redirect-to-
 * sign-in path, reload, and loop. The current mount point is
 * `<InsightsBootstrapTrigger>` inside `MainLayout`'s `<AuthGuard>`.
 *
 * Retry + flag-on-success: the very first request after sign-in can race the
 * auth interceptor — the WorkOS access-token function is registered via
 * `useLayoutEffect` in `WorkosAuthBridge` and the actual token may not be
 * available on the first axios request. We saw the cold-start 401 in dev
 * (incident: trigger fired but middleware rejected before the handler ran).
 * To make the bootstrap robust:
 *   - The session flag is set ONLY on success, so a 401 doesn't permanently
 *     block subsequent attempts.
 *   - The mutation retries twice with a small backoff. The endpoint is
 *     idempotent (DB short-circuit + Temporal dedup), so extra calls are
 *     harmless if the first attempt actually succeeded.
 *   - A per-mount ref guards against firing more than once from the same
 *     component instance (e.g., StrictMode double-invocation).
 */
export function useInsightsBootstrap(): void {
  const hasAttemptedThisMount = useRef(false);

  const { mutate } = useTriggerInsightsGenerationV1InsightsGeneratePost({
    mutation: {
      retry: 2,
      retryDelay: 1500,
      onSuccess: () => {
        sessionStorage.setItem(SESSION_FIRED_KEY, 'true');
      },
      onError: (error) => {
        sentryService.captureException(error as Error, { operation: 'useInsightsBootstrap' });
      },
    },
  });

  useEffect(() => {
    if (hasAttemptedThisMount.current) return;
    if (sessionStorage.getItem(SESSION_FIRED_KEY) === 'true') return;
    hasAttemptedThisMount.current = true;
    mutate();
  }, [mutate]);
}
